import {
  bindExternalStoreMessage,
  getExternalStoreMessages,
  type ThreadRuntime,
} from '@assistant-ui/react';
import type { UIMessage } from 'ai';

/** A saved edit does not cancel the old SDK stream. Keep its updates on
 * their actual original parent while the visible selection stays repaired. */
export function preserveRepairDuringOriginal(
  thread: ThreadRuntime,
  originalParentId: string,
  originalAnswerId: string | undefined,
  headId: string,
  isCurrentOwner: () => boolean,
): () => void {
  let restoring = false;
  const release = thread.subscribe(() => {
    if (restoring) return;
    if (!isCurrentOwner()) {
      release();
      return;
    }
    const state = thread.getState();
    const user = [...state.messages].reverse().find((message) => message.role === 'user');
    if (user?.id !== headId && user?.id !== originalParentId) {
      release();
      return;
    }
    const incoming = state.messages.at(-1);
    if (
      !originalAnswerId &&
      incoming?.role === 'assistant' &&
      getExternalStoreMessages<UIMessage>(incoming).some((message) => message.role === 'assistant')
    )
      originalAnswerId = incoming.id;
    if (incoming && incoming.role === 'assistant' && incoming.id === originalAnswerId) {
      const retained = {
        id: incoming.id,
        role: incoming.role,
        createdAt: incoming.createdAt,
        content: incoming.content,
        status: incoming.status,
        metadata: { ...incoming.metadata, isOptimistic: false },
      };
      // The SDK mutates its active wire object. Retain an independent native
      // snapshot so revisiting this branch invalidates its converter cache.
      bindExternalStoreMessage(
        retained,
        getExternalStoreMessages<UIMessage>(incoming).map((message) => ({
          ...message,
          parts: message.parts.map((part) => ({ ...part })),
        })),
      );
      const repository = thread.export();
      restoring = true;
      try {
        thread.import({
          headId,
          messages: [
            ...repository.messages.filter(({ message }) => message.id !== originalAnswerId),
            { parentId: originalParentId, message: retained },
          ],
        });
      } finally {
        restoring = false;
      }
    }
    if (!state.isRunning) release();
  });
  return release;
}
